import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, FileCheck, HeartHandshake, BadgeCheck, Landmark } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const IMG = {
  stag: "https://images.unsplash.com/photo-1526515579900-98518e7862cc?q=85&w=1600&auto=format&fit=crop",
  night: "https://images.unsplash.com/photo-1614398306313-aa5a4c465e9e?q=85&w=1200&auto=format&fit=crop",
};

const LICENCES = [
  { icon: BadgeCheck, code: "LIC/FA-AB", title: "Firearms Licence Cat A · B", body: "Rimfire and centrefire endorsements for professional vertebrate pest work." },
  { icon: Landmark, code: "LIC/COL-VIC", title: "Commercial Operator Licence", body: "Authorised commercial vertebrate pest controller under Agriculture Victoria." },
  { icon: FileCheck, code: "PRM/OPP-VIC", title: "Open Public Place Permits", body: "Permitted to operate in approved public places — golf courses, reserves and council land." },
  { icon: Award, code: "CRT/AHCPM304", title: "Humane Dispatch Certified", body: "Trained and assessed in the humane destruction of animals under national standards." },
  { icon: ShieldCheck, code: "INS/PL-20M", title: "$20M Public Liability", body: "Comprehensive cover carried on every job, on every property, every night." },
  { icon: FileCheck, code: "PRM/ACUP", title: "ACUP Chemical Permits", body: "Agricultural Chemical User Permits for 1080 and PAPP baiting programs." },
];

const ETHICS = [
  "Single-shot dispatch is the only acceptable outcome — wounded and lost is a failure.",
  "Every animal is tallied and every night is GPS-logged for the landholder's records.",
  "We overwatch livestock and crops across the operational area for the life of the program.",
  "Every animal is positively identified before a shot is taken — all non-target species in the operational area are protected.",
  "We leave gates, ground and goodwill exactly as we found them.",
];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-16">
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>About · The Operator Behind The Optics</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-bone sm:text-5xl lg:text-6xl">
            On the land.
            <br />
            <span className="text-thermal">On your side.</span>
          </h1>
        </FadeIn>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8" data-testid="about-story">
        <div className="grid items-start gap-12 lg:grid-cols-12">
          <FadeIn className="lg:col-span-7">
            <h2 className="font-display text-2xl font-bold tracking-tight text-bone sm:text-3xl">
              Built in regional Victoria, for regional Victoria
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-euca">
              <p>
                Victorian Pest and Game Services grew out of a passion to protect property,
                livestock and crops from destruction by vertebrate pest animals — watching
                foxes work lambing ewes at first light and pig mobs plough through a season's
                profit overnight. We started this business because landholders deserved a
                contractor who treats their place like his own.
              </p>
              <p>
                Today we run thermal-equipped night operations across all parts of Victoria
                and southern NSW — for family farms, corporate stations, councils and golf
                courses alike. Every job is planned in writing, executed under full
                licensing, and closed out with a written return.
              </p>
              <p>
                No bravado, no guesswork, no animals unaccounted for. Just professional
                vertebrate pest control, done properly.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-5">
            <div className="relative overflow-hidden border border-thermal/15">
              <img src={IMG.stag} alt="Kangaroos grazing on open Australian pasture" className="aspect-[4/5] w-full object-cover grayscale-[0.35]" />
              <div className="absolute bottom-0 left-0 right-0 bg-obsidian/85 p-5 backdrop-blur">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal">The Country We Work</p>
                <p className="mt-2 font-display text-sm font-bold uppercase tracking-wide text-bone">
                  Open pasture &amp; bush edge — kangaroo country
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <Marquee items={["Licensed", "Insured", "Humane", "Reported In Writing", "Thermal Equipped", "VIC & Southern NSW"]} />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="licensing-grid">
        <FadeIn>
          <Eyebrow>Licensing &amp; Credentials</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-bone sm:text-3xl lg:text-4xl">
            Paperwork that lets us work anywhere
          </h2>
        </FadeIn>
        <div className="mt-14 grid gap-px border border-thermal/15 bg-thermal/15 sm:grid-cols-2 lg:grid-cols-3">
          {LICENCES.map((l, i) => (
            <FadeIn key={l.code} delay={i * 0.06}>
              <div data-testid={`licence-card-${l.code.toLowerCase().replace(/[\/]/g, "-")}`} className="group h-full bg-obsidian p-8 transition-colors duration-300 hover:bg-olive">
                <div className="flex items-center justify-between">
                  <l.icon className="h-6 w-6 text-thermal" />
                  <p className="font-mono text-[10px] tracking-[0.2em] text-thermal/60">{l.code}</p>
                </div>
                <h3 className="mt-6 font-display text-lg font-bold tracking-tight text-bone">{l.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-euca">{l.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="border-t border-thermal/15 bg-field/40" data-testid="ethics-section">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12">
            <FadeIn className="lg:col-span-5">
              <Eyebrow>The Ethics Manifesto</Eyebrow>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-bone sm:text-3xl lg:text-4xl">
                <HeartHandshake className="mr-3 inline h-8 w-8 text-thermal" />
                Humane is non-negotiable
              </h2>
              <div className="relative mt-10 overflow-hidden border border-thermal/15">
                <img src={IMG.night} alt="Kangaroo portrait — a protected non-target species" className="aspect-video w-full object-cover object-[center_25%] grayscale-[0.5]" />
              </div>
            </FadeIn>
            <div className="lg:col-span-7">
              <div className="space-y-0 lg:mt-16">
                {ETHICS.map((e, i) => (
                  <FadeIn key={e} delay={i * 0.1}>
                    <div className="flex gap-6 border-t border-thermal/15 py-8 last:border-b">
                      <p className="font-display text-3xl font-black text-thermal/25">{`0${i + 1}`}</p>
                      <p className="max-w-xl text-base leading-relaxed text-bone/90">{e}</p>
                    </div>
                  </FadeIn>
                ))}
              </div>
              <FadeIn delay={0.3}>
                <Link
                  to="/contact"
                  data-testid="about-contact-button"
                  className="group mt-10 inline-flex items-center gap-2 border border-thermal bg-thermal px-6 py-3 font-mono text-xs font-medium uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-transparent hover:text-thermal active:scale-95"
                >
                  Work With Us
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
